import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Monitor, Smartphone, Tablet, TrendingUp } from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import api, { formatApiErrorDetail } from "@/lib/api";

const WINDOWS = [
  { key: "7d", label: "7 Days" },
  { key: "30d", label: "30 Days" },
  { key: "90d", label: "90 Days" },
  { key: "all", label: "All Time" },
];

const DEVICE_ICONS = {
  Desktop: <Monitor size={13} />,
  Mobile: <Smartphone size={13} />,
  Tablet: <Tablet size={13} />,
};

/**
 * iter72o · Join Page Analytics — SA-only.
 *
 * Deliberately minimal: KPI row, one line chart with 7d/30d/90d/all
 * filters, top sources table, device breakdown, recent visits table.
 */
export default function SuperAdminJoinAnalyticsPage() {
  const [windowKey, setWindowKey] = useState("7d");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get("/super-admin/analytics/join-page", { params: { window: windowKey } })
      .then((r) => { if (!cancelled) { setData(r.data); setError(null); } })
      .catch((err) => {
        if (!cancelled) setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [windowKey]);

  const series = useMemo(() => {
    if (!data?.series) return [];
    return data.series.map((r) => ({
      day: r.day,
      short: r.day.slice(5), // MM-DD in chart axis
      views: r.views,
    }));
  }, [data]);

  const totalInWindow = useMemo(() => series.reduce((n, r) => n + r.views, 0), [series]);

  return (
    <div className="bg-cream min-h-screen" data-testid="sa-join-analytics-page">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-10">
        <Link
          to="/super-admin"
          data-testid="sa-join-analytics-back"
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.22em] text-[#1A422D] hover:underline"
        >
          <ChevronLeft size={14} /> Back to Command Center
        </Link>

        <div className="mt-4 flex items-end justify-between gap-6 flex-wrap">
          <div>
            <div className="overline">Public Traffic</div>
            <h1 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A] mt-2 leading-none">
              Join Page Analytics
            </h1>
            <p className="text-[#4A4A4A] mt-3 max-w-2xl">
              Anonymised page views for every <code>/join/:club</code> landing. Session-deduped;
              no PII collected — visitor uniqueness is a rotating salted hash.
            </p>
          </div>
          <WindowFilter value={windowKey} onChange={setWindowKey} />
        </div>

        {loading && <div className="mt-8 overline text-[#7A7A7A]">Loading…</div>}
        {error && (
          <div className="mt-6 px-4 py-3 text-sm border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a]">{error}</div>
        )}

        {data && (
          <>
            <section className="mt-8 grid grid-cols-2 lg:grid-cols-6 gap-4" data-testid="sa-join-kpis">
              <Kpi label="Views Today" value={data.kpis.total_today} testId="kpi-today" />
              <Kpi label="Last 7 Days" value={data.kpis.total_7d} testId="kpi-7d" />
              <Kpi label="Last 30 Days" value={data.kpis.total_30d} testId="kpi-30d" />
              <Kpi label="All-Time" value={data.kpis.total_all} testId="kpi-all" />
              <Kpi label="Unique Visitors" value={data.kpis.unique_visitors_all_time} testId="kpi-unique"
                   hint="All-time distinct fingerprints" />
              <Kpi label="Returning" value={data.kpis.returning_visitors_window} testId="kpi-returning"
                   hint="Second visit or later" />
            </section>

            <section className="mt-8 surface-card p-6 sm:p-7" data-testid="sa-join-chart">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="overline">Trend</div>
                  <h2 className="font-serif text-2xl mt-1 text-[#1A1A1A]">Join Page Views Over Time</h2>
                </div>
                <div className="text-xs text-[#7A7A7A] inline-flex items-center gap-1.5">
                  <TrendingUp size={13} className="text-[#B59A5A]" />
                  <span className="uppercase tracking-wider">Total in window: </span>
                  <span className="font-mono text-[#1A422D]">{totalInWindow}</span>
                </div>
              </div>
              <div className="mt-5" style={{ width: "100%", height: 280 }}>
                {series.length === 0 ? (
                  <EmptyState>No page views recorded in this window yet.</EmptyState>
                ) : (
                  <ResponsiveContainer>
                    <LineChart data={series} margin={{ top: 10, right: 20, bottom: 5, left: -10 }}>
                      <CartesianGrid stroke="#E5E3D8" strokeDasharray="3 3" />
                      <XAxis
                        dataKey="short"
                        stroke="#7A7A7A"
                        tick={{ fontSize: 11 }}
                        interval="preserveStartEnd"
                      />
                      <YAxis
                        stroke="#7A7A7A"
                        tick={{ fontSize: 11 }}
                        allowDecimals={false}
                        width={40}
                      />
                      <Tooltip
                        contentStyle={{
                          background: "#FFFFFF",
                          border: "1px solid #E5E3D8",
                          fontSize: 12,
                        }}
                        labelStyle={{ color: "#1A422D", fontWeight: 600 }}
                        formatter={(v) => [v, "Views"]}
                        labelFormatter={(_l, entries) => entries?.[0]?.payload?.day || ""}
                      />
                      <Line
                        type="monotone"
                        dataKey="views"
                        stroke="#1A422D"
                        strokeWidth={2}
                        dot={{ r: 3, fill: "#B59A5A", stroke: "#1A422D", strokeWidth: 1 }}
                        activeDot={{ r: 5 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </section>

            <section className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
              <BreakdownCard
                title="Top Traffic Sources"
                overline="Referrers"
                rows={data.top_sources}
                emptyLabel="No traffic yet."
                testId="sa-join-sources"
              />
              <BreakdownCard
                title="Device Type"
                overline="Endpoints"
                rows={data.device_breakdown}
                emptyLabel="No device data yet."
                withIcon
                testId="sa-join-devices"
              />
            </section>

            <section className="mt-8 surface-card overflow-x-auto" data-testid="sa-join-recent">
              <div className="px-6 py-5 border-b border-[#E5E3D8]">
                <div className="overline">Live</div>
                <h2 className="font-serif text-2xl mt-1 text-[#1A1A1A]">Recent Visits</h2>
                <p className="text-xs text-[#7A7A7A] mt-1">Latest 25 in the selected window.</p>
              </div>
              <table className="w-full text-left">
                <thead className="bg-[#F3F1E9]">
                  <tr className="text-xs uppercase tracking-wider text-[#4A4A4A]">
                    <th className="px-6 py-3">When</th>
                    <th className="px-6 py-3">Club Slug</th>
                    <th className="px-6 py-3">Source</th>
                    <th className="px-6 py-3">Referrer</th>
                    <th className="px-6 py-3">Device</th>
                    <th className="px-6 py-3">Visitor</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-6 py-10 text-center text-[#7A7A7A]">
                        No recent visits.
                      </td>
                    </tr>
                  )}
                  {data.recent.map((r) => (
                    <tr key={r.view_id} className="border-t border-[#E5E3D8]" data-testid={`sa-join-recent-row-${r.view_id}`}>
                      <td className="px-6 py-3 text-xs text-[#4A4A4A] whitespace-nowrap">
                        {new Date(r.at).toLocaleString()}
                      </td>
                      <td className="px-6 py-3 text-sm font-mono text-[#1A422D]">{r.slug || "—"}</td>
                      <td className="px-6 py-3 text-sm">{r.source}</td>
                      <td className="px-6 py-3 text-xs text-[#4A4A4A] font-mono">{r.referrer_host || "—"}</td>
                      <td className="px-6 py-3 text-sm inline-flex items-center gap-1.5">
                        {DEVICE_ICONS[r.device] || null} {r.device}
                      </td>
                      <td className="px-6 py-3 text-xs">
                        {r.is_returning ? (
                          <span className="px-2 py-0.5 border border-[#B59A5A] text-[#8a6f33] bg-[#FBF6E8] uppercase tracking-wider">
                            Returning
                          </span>
                        ) : (
                          <span className="text-[#7A7A7A]">New</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          </>
        )}
      </div>
    </div>
  );
}

function WindowFilter({ value, onChange }) {
  return (
    <div className="inline-flex border border-[#E5E3D8] bg-white" data-testid="sa-join-window-filter">
      {WINDOWS.map((w) => (
        <button
          key={w.key}
          type="button"
          onClick={() => onChange(w.key)}
          data-testid={`sa-join-window-${w.key}`}
          className={`px-4 py-2 text-xs uppercase tracking-[0.18em] font-semibold transition-colors ${
            value === w.key
              ? "bg-[#1A422D] text-[#F9F8F4]"
              : "text-[#1A422D] hover:bg-[#F3F1E9]"
          }`}
        >
          {w.label}
        </button>
      ))}
    </div>
  );
}

function Kpi({ label, value, hint, testId }) {
  return (
    <div data-testid={testId} className="surface-card p-5">
      <div className="overline">{label}</div>
      <div className="font-serif text-3xl mt-2 text-[#1A1A1A]">{value ?? 0}</div>
      {hint && <div className="text-xs text-[#7A7A7A] mt-1">{hint}</div>}
    </div>
  );
}

function BreakdownCard({ title, overline, rows, emptyLabel, withIcon, testId }) {
  const total = rows.reduce((n, r) => n + r.views, 0);
  return (
    <div className="surface-card p-6 sm:p-7" data-testid={testId}>
      <div className="overline">{overline}</div>
      <h2 className="font-serif text-2xl mt-1 text-[#1A1A1A]">{title}</h2>
      {rows.length === 0 ? (
        <p className="text-sm text-[#7A7A7A] mt-4">{emptyLabel}</p>
      ) : (
        <ul className="mt-5 space-y-3">
          {rows.map((r) => {
            const pct = total > 0 ? Math.round((r.views / total) * 100) : 0;
            return (
              <li key={r.label} className="text-sm">
                <div className="flex items-center justify-between gap-3">
                  <span className="inline-flex items-center gap-2 text-[#1A1A1A]">
                    {withIcon && DEVICE_ICONS[r.label]} {r.label}
                  </span>
                  <span className="font-mono text-xs text-[#7A7A7A]">
                    {r.views} · {pct}%
                  </span>
                </div>
                <div className="mt-1.5 h-1.5 bg-[#F3F1E9]">
                  <div
                    className="h-1.5 bg-[#1A422D] transition-all"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function EmptyState({ children }) {
  return (
    <div className="h-full flex items-center justify-center text-sm text-[#7A7A7A]">
      {children}
    </div>
  );
}
